import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import ShellSaac, { useVoltar } from '../../../components/shell/ShellSaac';
import {
  Botao,
  Chip,
  DataList,
  EmptyState,
  ErrorState,
  Field,
  FormSection,
  Input,
  Nota,
  PageHeader,
  Skeleton,
  Tabs,
  idDaAba,
  idDoPainel,
} from '../../../ui';

import FormularioUsuario, { DadosUsuarioForm } from '../../../components/usuario/FormularioUsuario';
import ModalConfirmacao from '../../../components/modal/confirmacao/ModalConfirmacao';
import ResumoRegistro from '../../../components/ti/ResumoRegistro';

import { RootState } from '../../../store/store';
import { TIUSUARIO, TIUSUARIOS } from '../routes';
import {
  SessaoUsuario,
  UsuarioDetalhado,
  alterarSituacaoUsuario,
  atualizarUsuario,
  buscarUsuarioPorId,
  encerrarSessoesUsuario,
  listarSessoesUsuario,
} from '../../../api/user/userApi';
import { signupUser, tiResetPassword } from '../../../api/auth/authApi';
import { formatCpfCnpj } from '../../../utils/formartCPF';
import { formatarDataHora } from '../../../utils/data';
import { REGRA_SENHA, gerarSenhaForte, senhaForte } from '../../../utils/senha';
import {
  motivoSemAlterarSituacao,
  motivoSemEdicao,
  podeAlterarSituacao,
  podeEditar,
  rotuloPapel,
  rotuloSituacao,
  varianteDoPapel,
} from '../../../utils/usuarioRegras';
import { extrairMensagemErro } from '../../../utils/apiError';

/**
 * A ficha do usuário — **cria e edita com o mesmo formulário** (at-68, frente D).
 *
 * `/ti/usuarios/novo` abre em modo criar (sem abas: não há senha temporária, sessão nem
 * situação de quem ainda não existe); `/ti/usuarios/:id` abre em modo editar, com duas
 * abas — **Dados** e **Acesso**.
 *
 * ## Por que sub-rota e não modal
 *
 * São ~8 campos, dois seletores de vínculo e três blocos de acesso: é uma tela, não um
 * diálogo. E URL própria dá "Voltar" do navegador, recarregar e link que um TI manda para
 * outro. Precedente: `/adesoes/consultar` (at-56).
 *
 * ## As travas, e por que elas DIZEM o motivo
 *
 * TI não edita ADMIN e ninguém desativa a própria conta. As duas regras são do backend
 * (403/400), mas a tela as aplica antes **e mostra o motivo em texto** — desabilitar sem
 * explicar deixa o TI achando que a tela quebrou, e deixar habilitado o faz descobrir no
 * clique.
 */

type Estado = 'carregando' | 'ok' | 'erro';
type Aba = 'dados' | 'acesso';

const ID_BASE = 'usuario-ficha';

const ABAS = [
  { id: 'dados', rotulo: 'Dados' },
  { id: 'acesso', rotulo: 'Acesso' },
];

const UsuarioFichaPage: React.FC = () => {
  const navigate = useNavigate();
  const voltar = useVoltar(TIUSUARIOS());
  const { id } = useParams<{ id: string }>();

  const modo: 'criar' | 'editar' = !id || id === 'novo' ? 'criar' : 'editar';

  const requesterUserType = useSelector((s: RootState) => s.auth.userType);
  const requesterId = useSelector((s: RootState) => s.auth.userId);

  const [usuario, setUsuario] = useState<UsuarioDetalhado | null>(null);
  const [estado, setEstado] = useState<Estado>(modo === 'criar' ? 'ok' : 'carregando');
  const [erro, setErro] = useState('');
  const [statusErro, setStatusErro] = useState<number | null>(null);
  const [aba, setAba] = useState<Aba>('dados');

  const [salvando, setSalvando] = useState(false);
  const [erroSalvar, setErroSalvar] = useState('');

  const [sessoes, setSessoes] = useState<SessaoUsuario[]>([]);
  const [carregandoSessoes, setCarregandoSessoes] = useState(false);
  const [erroSessoes, setErroSessoes] = useState('');

  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [erroSenha, setErroSenha] = useState('');
  const [definindoSenha, setDefinindoSenha] = useState(false);

  const [confirmacao, setConfirmacao] = useState<'desativar' | 'reativar' | 'sessoes' | null>(null);
  const [processando, setProcessando] = useState(false);

  const carregar = useCallback(async () => {
    if (modo === 'criar') return;

    setEstado('carregando');
    try {
      setUsuario(await buscarUsuarioPorId(id as string));
      setEstado('ok');
    } catch (falha: any) {
      setStatusErro(falha?.status ?? null);
      setErro(extrairMensagemErro(falha, 'Não foi possível carregar o usuário.'));
      setEstado('erro');
    }
  }, [id, modo]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const carregarSessoes = useCallback(async () => {
    if (modo === 'criar' || !id) return;

    setCarregandoSessoes(true);
    setErroSessoes('');
    try {
      setSessoes(await listarSessoesUsuario(id));
    } catch (falha) {
      setSessoes([]);
      setErroSessoes(extrairMensagemErro(falha, 'Não foi possível carregar as sessões.'));
    } finally {
      setCarregandoSessoes(false);
    }
  }, [id, modo]);

  // As sessões só são buscadas quando a aba Acesso abre: são dado de uma aba, e
  // carregá-las junto com a ficha custaria uma requisição que a maioria das visitas
  // (que vêm editar um telefone) não usa.
  useEffect(() => {
    if (aba === 'acesso') void carregarSessoes();
  }, [aba, carregarSessoes]);

  const podeMexer = podeEditar(requesterUserType, usuario?.userType);
  const motivoTravado = modo === 'editar' ? motivoSemEdicao(requesterUserType, usuario?.userType) : null;
  const editandoProprio = modo === 'editar' && !!usuario && usuario.id === requesterId;

  const situacaoLiberada =
    modo === 'editar' &&
    !!usuario &&
    podeAlterarSituacao(requesterUserType, usuario.userType, requesterId, usuario.id);
  const motivoSituacao =
    modo === 'editar' && usuario
      ? motivoSemAlterarSituacao(requesterUserType, usuario.userType, requesterId, usuario.id)
      : null;

  const salvar = async (dados: Partial<DadosUsuarioForm>) => {
    setErroSalvar('');

    if (modo === 'criar') {
      setSalvando(true);
      try {
        const resposta = await signupUser({
          email: dados.email ?? '',
          password: dados.password ?? '',
          confirmPassword: dados.confirmPassword ?? '',
          volunteerCode: dados.volunteerCode || undefined,
          cpfCnpj: dados.cpfCnpj ?? '',
          nomeCompleto: dados.nomeCompleto ?? '',
          celular: dados.celular ?? '',
          userType: dados.userType,
          codigo_regionais: dados.codigo_regionais,
          codigo_cooperativas: dados.codigo_cooperativas,
        });

        toast.success('Usuário criado com sucesso');

        // O `id` passou a vir na resposta (at-68) justamente para abrir a ficha do
        // usuário novo. Sem ele, a única saída seria voltar à lista e procurá-lo.
        if (resposta?.id) {
          navigate(TIUSUARIO(resposta.id), { replace: true });
        } else {
          navigate(TIUSUARIOS(), { replace: true });
        }
      } catch (falha) {
        // 🔴 O erro fica JUNTO do formulário, não só num toast que passa: é aqui que
        // aparece "e-mail já em uso", e o TI precisa dele na tela para corrigir o campo.
        setErroSalvar(extrairMensagemErro(falha, 'Não foi possível criar o usuário.'));
        setSalvando(false);
      }
      return;
    }

    if (Object.keys(dados).length === 0) {
      toast.info('Nenhuma alteração para salvar.');
      return;
    }

    setSalvando(true);
    try {
      const atualizado = await atualizarUsuario(id as string, dados);
      setUsuario(atualizado);
      toast.success('Usuário atualizado com sucesso');

      if (editandoProprio) {
        toast.info('Você editou a própria conta. Saia e entre novamente para atualizar a sessão.');
      }
    } catch (falha) {
      setErroSalvar(extrairMensagemErro(falha, 'Não foi possível salvar as alterações.'));
    } finally {
      setSalvando(false);
    }
  };

  const definirSenhaTemporaria = async () => {
    setErroSenha('');

    if (!novaSenha || !confirmarSenha) {
      setErroSenha('Preencha os dois campos de senha.');
      return;
    }
    if (novaSenha !== confirmarSenha) {
      setErroSenha('As senhas não coincidem.');
      return;
    }
    if (!senhaForte(novaSenha)) {
      setErroSenha(REGRA_SENHA);
      return;
    }

    setDefinindoSenha(true);
    try {
      await tiResetPassword({
        targetUserId: id as string,
        newPassword: novaSenha,
        confirmNewPassword: confirmarSenha,
      });

      setNovaSenha('');
      setConfirmarSenha('');
      toast.success('Senha temporária definida. O usuário vai trocá-la no próximo acesso.');

      if (editandoProprio) {
        toast.info('A sua própria sessão foi encerrada nos outros dispositivos.');
      }

      void carregarSessoes();
      void carregar();
    } catch (falha) {
      setErroSenha(extrairMensagemErro(falha, 'Não foi possível definir a senha.'));
    } finally {
      setDefinindoSenha(false);
    }
  };

  const confirmarAcao = async () => {
    if (!usuario || !confirmacao) return;

    setProcessando(true);
    try {
      if (confirmacao === 'sessoes') {
        const { sessoesEncerradas } = await encerrarSessoesUsuario(usuario.id);
        toast.success(
          sessoesEncerradas === 0
            ? 'Nenhuma sessão aberta para encerrar.'
            : `${sessoesEncerradas} sessão(ões) encerrada(s).`,
        );
        void carregarSessoes();
      } else {
        const ativo = confirmacao === 'reativar';
        const atualizado = await alterarSituacaoUsuario(usuario.id, ativo);
        setUsuario(atualizado);

        toast.success(
          ativo
            ? 'Usuário reativado. Ele já consegue entrar.'
            : `Usuário desativado.${
                atualizado.sessoesEncerradas > 0
                  ? ` ${atualizado.sessoesEncerradas} sessão(ões) encerrada(s).`
                  : ''
              }`,
        );
        void carregarSessoes();
      }

      setConfirmacao(null);
    } catch (falha) {
      toast.error(extrairMensagemErro(falha, 'Não foi possível concluir a ação.'));
    } finally {
      setProcessando(false);
    }
  };

  const tituloDaTela =
    modo === 'criar' ? 'Novo usuário' : usuario?.nomeCompleto || usuario?.email || 'Usuário';

  const conteudo = () => {
    if (estado === 'carregando') return <Skeleton linhas={6} altura="tw-h-12" />;

    if (estado === 'erro') {
      // 404 e falha de rede pedem coisas diferentes: um é "este usuário não existe",
      // o outro é "tente de novo".
      if (statusErro === 404) {
        return (
          <EmptyState
            icone="sem-dados"
            titulo="Usuário não encontrado"
            descricao="O endereço aponta para um usuário que não existe mais."
            acao={
              <Botao variante="secundario" onClick={() => navigate(TIUSUARIOS())}>
                Voltar para a lista
              </Botao>
            }
          />
        );
      }

      return (
        <ErrorState
          mensagem="Não foi possível carregar o usuário."
          detalhe={erro}
          aoTentarNovamente={() => void carregar()}
        />
      );
    }

    const formulario = (
      <div className="tw-flex tw-flex-col tw-gap-4">
        {editandoProprio && (
          <Nota variante="atencao">
            Você está editando a <strong>sua própria conta</strong>. As mudanças de papel, nome ou
            código só aparecem na sua sessão depois que você sair e entrar de novo.
          </Nota>
        )}

        {motivoTravado && <Nota variante="atencao">{motivoTravado}</Nota>}

        {erroSalvar && (
          <Nota variante="atencao" aria-live="polite">
            {erroSalvar}
          </Nota>
        )}

        <FormularioUsuario
          // A `key` força o formulário a renascer quando o usuário carregado muda —
          // `valoresIniciais` é lido uma vez só, de propósito.
          key={usuario?.id ?? 'novo'}
          modo={modo}
          valoresIniciais={usuario}
          requesterUserType={requesterUserType}
          aoSalvar={dados => void salvar(dados)}
          salvando={salvando}
          desabilitado={modo === 'editar' && !podeMexer}
        />
      </div>
    );

    if (modo === 'criar') return formulario;

    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        {usuario && (
          <div className="tw-flex tw-flex-col tw-gap-3">
            <div className="tw-flex tw-flex-wrap tw-gap-1.5">
              <Chip variante={varianteDoPapel(usuario.userType)}>
                {rotuloPapel(usuario.userType)}
              </Chip>
              <Chip variante={rotuloSituacao(usuario).variante}>
                {rotuloSituacao(usuario).texto}
              </Chip>
            </div>

            <ResumoRegistro
              linhas={[
                { rotulo: 'E-mail', valor: usuario.email },
                { rotulo: 'Código de voluntário', valor: usuario.volunteerCode ?? '—' },
                {
                  rotulo: 'CPF / CNPJ',
                  valor: usuario.cpfCnpj ? formatCpfCnpj(usuario.cpfCnpj) : '—',
                },
                { rotulo: 'Criado em', valor: formatarDataHora(usuario.createdAt) ?? '—' },
                ...(usuario.desativadoEm
                  ? [
                      {
                        rotulo: 'Desativado em',
                        valor: formatarDataHora(usuario.desativadoEm) ?? '—',
                      },
                    ]
                  : []),
              ]}
            />
          </div>
        )}

        <Tabs
          idBase={ID_BASE}
          abas={ABAS}
          ativa={aba}
          aoTrocar={valor => setAba(valor as Aba)}
          rotuloAcessivel="Seções do usuário"
        />

        <div
          id={idDoPainel(ID_BASE)}
          role="tabpanel"
          aria-labelledby={idDaAba(ID_BASE, aba)}
          tabIndex={-1}
        >
          {aba === 'dados' ? formulario : painelDeAcesso()}
        </div>
      </div>
    );
  };

  const painelDeAcesso = () => (
    <div className="tw-flex tw-flex-col tw-gap-8">
      {/* Senha temporária — oculta quando o requisitante não pode mexer neste usuário
          (um TI diante de um ADMIN). O backend recusa desde a at-68; a tela não oferece. */}
      {podeMexer ? (
        <FormSection titulo="Senha temporária" icone="check">
          <Nota>
            Definir uma senha temporária <strong>aciona o primeiro acesso</strong> (o usuário terá
            de trocá-la ao entrar) e <strong>encerra as sessões abertas</strong> dele em todos os
            dispositivos.
          </Nota>

          {editandoProprio && (
            <Nota variante="atencao">
              Esta é a sua conta: definir a senha aqui derruba as suas outras sessões.
            </Nota>
          )}

          <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
            <Field rotulo="Nova senha" erro={erroSenha} dica={REGRA_SENHA}>
              <Input
                type="password"
                value={novaSenha}
                onChange={e => {
                  setNovaSenha(e.target.value);
                  setErroSenha('');
                }}
                autoComplete="new-password"
              />
            </Field>

            <Field rotulo="Confirmar senha">
              <Input
                type="password"
                value={confirmarSenha}
                onChange={e => {
                  setConfirmarSenha(e.target.value);
                  setErroSenha('');
                }}
                autoComplete="new-password"
              />
            </Field>
          </div>

          <div className="tw-flex tw-flex-wrap tw-gap-3">
            <Botao
              variante="fantasma"
              tamanho="sm"
              onClick={() => {
                const gerada = gerarSenhaForte();
                setNovaSenha(gerada);
                setConfirmarSenha(gerada);
                setErroSenha('');
              }}
            >
              Gerar senha forte
            </Botao>
            <Botao carregando={definindoSenha} onClick={() => void definirSenhaTemporaria()}>
              Definir senha temporária
            </Botao>
          </div>

          {novaSenha && (
            <p className="tw-m-0 tw-text-sm tw-text-texto-corpo">
              Senha a entregar: <strong className="tw-font-mono">{novaSenha}</strong>
            </p>
          )}
        </FormSection>
      ) : (
        <FormSection titulo="Senha temporária" icone="check">
          <Nota variante="atencao">
            {motivoTravado ?? 'Você não tem permissão para redefinir a senha deste usuário.'}
          </Nota>
        </FormSection>
      )}

      <FormSection titulo="Sessões abertas" icone="pessoa">
        <Nota>
          Cada linha é um aparelho onde o usuário entrou. Encerrar derruba todos sem trocar a senha
          dele.
        </Nota>

        {carregandoSessoes && <Skeleton linhas={3} altura="tw-h-12" />}

        {!carregandoSessoes && erroSessoes && (
          <ErrorState
            mensagem="Não foi possível carregar as sessões."
            detalhe={erroSessoes}
            aoTentarNovamente={() => void carregarSessoes()}
          />
        )}

        {!carregandoSessoes && !erroSessoes && sessoes.length === 0 && (
          <EmptyState
            icone="sem-dados"
            titulo="Nenhuma sessão registrada"
            descricao="O usuário ainda não entrou no sistema, ou as sessões antigas já foram apagadas."
          />
        )}

        {!carregandoSessoes && !erroSessoes && sessoes.length > 0 && (
          <>
            <DataList
              itens={sessoes}
              chave={s => s.id}
              rotuloAcessivel="Sessões do usuário"
              colunas={[
                {
                  id: 'dispositivo',
                  rotulo: 'Dispositivo',
                  principal: true,
                  celula: s => resumirDispositivo(s.userAgent),
                },
                {
                  id: 'ip',
                  rotulo: 'IP',
                  secundaria: true,
                  celula: s => s.ip ?? 'IP não registrado',
                },
                {
                  id: 'ultimoUso',
                  rotulo: 'Último uso',
                  celula: s => formatarDataHora(s.ultimoUsoEm) ?? '—',
                },
                {
                  id: 'estado',
                  rotulo: 'Estado',
                  celula: s => (
                    <Chip
                      variante={
                        s.estado === 'ATIVA' ? 'ok' : s.estado === 'EXPIRADA' ? 'neutro' : 'neutro'
                      }
                    >
                      {s.estado === 'ATIVA'
                        ? 'Ativa'
                        : s.estado === 'EXPIRADA'
                          ? 'Expirada'
                          : 'Revogada'}
                    </Chip>
                  ),
                },
              ]}
            />

            {podeMexer && sessoes.some(s => s.estado === 'ATIVA') && (
              <div className="tw-flex tw-flex-wrap tw-gap-3">
                <Botao variante="perigo" onClick={() => setConfirmacao('sessoes')}>
                  Encerrar todas as sessões
                </Botao>
              </div>
            )}
          </>
        )}
      </FormSection>

      <FormSection titulo="Situação do acesso" icone="alerta">
        {usuario?.ativo ? (
          <Nota>
            O usuário está <strong>ativo</strong> e consegue entrar no sistema.
          </Nota>
        ) : (
          <Nota variante="atencao">
            O usuário está <strong>inativo</strong>: não consegue entrar nem renovar a sessão. A
            carteira dele continua ligada ao código de voluntário.
          </Nota>
        )}

        {motivoSituacao && <Nota variante="atencao">{motivoSituacao}</Nota>}

        {situacaoLiberada && (
          <div className="tw-flex tw-flex-wrap tw-gap-3">
            {usuario?.ativo ? (
              <Botao variante="perigo" onClick={() => setConfirmacao('desativar')}>
                Desativar usuário
              </Botao>
            ) : (
              <Botao variante="secundario" onClick={() => setConfirmacao('reativar')}>
                Reativar usuário
              </Botao>
            )}
          </div>
        )}
      </FormSection>
    </div>
  );

  return (
    <ShellSaac
      cabecalho={
        <PageHeader titulo={tituloDaTela} sobretitulo="Usuários" aoVoltar={voltar} />
      }
    >
      <div className="tw-min-h-full tw-bg-shell">
        <div className="tw-mx-auto tw-w-full tw-max-w-4xl md:tw-p-6">
          <div className="tw-bg-white tw-p-4 md:tw-rounded-2xl md:tw-border md:tw-border-superficie-borda md:tw-p-6 md:tw-shadow-[0_2px_12px_rgba(0,0,0,0.09)]">
            {conteudo()}
          </div>
        </div>
      </div>

      <ModalConfirmacao
        aberto={confirmacao !== null}
        titulo={
          confirmacao === 'sessoes'
            ? 'Encerrar todas as sessões?'
            : confirmacao === 'reativar'
              ? 'Reativar este usuário?'
              : 'Desativar este usuário?'
        }
        mensagem={
          confirmacao === 'sessoes'
            ? 'O usuário será desconectado em todos os aparelhos e precisará entrar de novo. A senha dele não muda.'
            : confirmacao === 'reativar'
              ? 'Ele volta a conseguir entrar no sistema imediatamente.'
              : 'Ele deixa de conseguir entrar. As sessões abertas são encerradas agora, e o acesso de quem estiver com o aplicativo aberto termina em até 6 horas.'
        }
        detalhe={
          confirmacao === 'desativar'
            ? 'Nada é apagado: os associados, cotações e contratos dele continuam no sistema, ligados ao código de voluntário.'
            : undefined
        }
        destrutivo={confirmacao !== 'reativar'}
        processando={processando}
        rotuloConfirmar={
          confirmacao === 'sessoes'
            ? 'Encerrar sessões'
            : confirmacao === 'reativar'
              ? 'Reativar'
              : 'Desativar'
        }
        onConfirmar={() => void confirmarAcao()}
        onCancelar={() => setConfirmacao(null)}
      />
    </ShellSaac>
  );
};

/**
 * O `user-agent` cru é ilegível numa tabela. Reduz ao que o TI usa para reconhecer o
 * aparelho — e nunca esconde o dado por completo: sem correspondência, mostra o começo
 * da string em vez de "desconhecido".
 */
function resumirDispositivo(userAgent: string | null): string {
  if (!userAgent) return 'Dispositivo não identificado';

  const sistema = /Android/i.test(userAgent)
    ? 'Android'
    : /iPhone|iPad|iOS/i.test(userAgent)
      ? 'iPhone/iPad'
      : /Windows/i.test(userAgent)
        ? 'Windows'
        : /Mac OS/i.test(userAgent)
          ? 'Mac'
          : /Linux/i.test(userAgent)
            ? 'Linux'
            : null;

  const navegador = /Edg\//i.test(userAgent)
    ? 'Edge'
    : /Chrome\//i.test(userAgent)
      ? 'Chrome'
      : /Firefox\//i.test(userAgent)
        ? 'Firefox'
        : /Safari\//i.test(userAgent)
          ? 'Safari'
          : null;

  if (sistema && navegador) return `${navegador} no ${sistema}`;
  if (sistema) return sistema;
  if (navegador) return navegador;

  return userAgent.slice(0, 40);
}

export default UsuarioFichaPage;
